import gql from 'graphql-tag';

const CREATE_MILESTONE_TASK = gql`
  mutation(
    $title: String
    $description: String
    $status: String
    $techGroup: GroupInput
    $technician: UserInput
    $estimatedTime: Int
    $scheduledStartDate: String
    $dueDate: String
    $milestone: MilestoneInput
    $project: ProjectInput
    $taskOrder: Int
    $priority: String
  ) {
    createTask(
      input: {
        title: $title
        description: $description
        technician: $technician
        techGroup: $techGroup
        estimatedTime: $estimatedTime
        status: $status
        scheduledStartDate: $scheduledStartDate
        dueDate: $dueDate
        milestone: $milestone
        project: $project
        taskOrder: $taskOrder
        priority: $priority
      }
    ) {
      taskId
      taskOrder
      displayId
      title
      description
      status
      priority
      estimatedTime
      scheduledStartDate
      dueDate
      techGroup {
        groupId
        name
      }
      milestone {
        milestoneId
      }
      technician {
        userId
        name
        firstName
        lastName
      }
    }
  }
`;

const CREATE_TASK = gql`
  mutation(
    $title: String
    $description: String
    $techGroup: GroupInput
    $technician: UserInput
    $ticket: TicketInput
    $estimatedTime: Int
    $inlineImages: [String]
    $scheduledStartDate: String
    $dueDate: String
    $status: String
    $priority: String
  ) {
    createTask(
      input: {
        title: $title
        description: $description
        ticket: $ticket
        technician: $technician
        techGroup: $techGroup
        estimatedTime: $estimatedTime
        inlineImages: $inlineImages
        scheduledStartDate: $scheduledStartDate
        dueDate: $dueDate
        status: $status
        priority: $priority
      }
    ) {
      taskId
      displayId
      title
      description
      estimatedTime
      priority
      techGroup {
        groupId
        name
      }
      ticket {
        ticketId
      }
      technician {
        userId
        name
        firstName
        lastName
      }
    }
  }
`;

const CREATE_TASKS = gql`
  mutation($taskInput: [TaskInput!]!) {
    createTasks(input: $taskInput) {
      taskId
      displayId
      title
      description
      status
      priority
      estimatedTime
      scheduledStartDate
      dueDate
      techGroup {
        groupId
        name
      }
      milestone {
        milestoneId
        name
      }
      technician {
        userId
        name
        firstName
        lastName
      }
    }
  }
`;

const UPDATE_TASK = gql`
  mutation($taskId: ID!, $module: String!, $input: TaskInput!) {
    updateTask(taskId: $taskId, module: $module, input: $input) {
      taskId
      taskOrder
      displayId
      title
      description
      estimatedTime
      status
      priority
      scheduledStartDate
      dueDate
      techGroup {
        groupId
        name
      }
      milestone {
        milestoneId
      }
      technician {
        userId
        name
        firstName
        lastName
      }
      ticket {
        ticketId
        subject
        displayId
        loggedInUserAsFollower {
          ticketPermission
        }
      }
      module
      project {
        projectId
        title
        loggedInUserRole {
          roleId
        }
      }
      workItem {
        workId
        displayId
        subject
        module
        client {
          accountId
          name
        }
        site {
          name
          id
        }
      }
      ticketPermission
    }
  }
`;

const DELETE_TASK = gql`
  mutation($taskId: ID!, $module: String!) {
    deleteTask(taskId: $taskId, module: $module)
  }
`;

const DELETE_TASK_TEMPLATE = gql`
  mutation($taskTemplateId: ID!) {
    deleteTaskTemplate(taskTemplateId: $taskTemplateId)
  }
`;

const UPDATE_TASK_TEMPLATE = gql`
  mutation(
    $taskTemplateId: ID!
    $title: String
    $description: String
    $techGroup: GroupInput
    $technician: UserInput
    $estimatedTime: Int
  ) {
    updateTaskTemplate(
      taskTemplateId: $taskTemplateId
      input: {
        taskTemplateId: $taskTemplateId
        title: $title
        description: $description
        techGroup: $techGroup
        technician: $technician
        estimatedTime: $estimatedTime
      }
    ) {
      taskTemplateId
      dueDate
      scheduledStartDate
      title
      description
      estimatedTime
      techGroup {
        groupId
      }
      technician {
        userId
        name
        firstName
        lastName
      }
    }
  }
`;

const CREATE_TASK_TEMPLATE = gql`
  mutation(
    $title: String
    $description: String
    $techGroup: GroupInput
    $technician: UserInput
    $estimatedTime: Int
    $dueDate: JSON
    $scheduledStartDate: JSON
    $shared: Boolean
  ) {
    createTaskTemplate(
      input: {
        title: $title
        description: $description
        technician: $technician
        techGroup: $techGroup
        estimatedTime: $estimatedTime
        dueDate: $dueDate
        scheduledStartDate: $scheduledStartDate
        shared: $shared
      }
    ) {
      taskTemplateId
      dueDate
      scheduledStartDate
      title
      description
      estimatedTime
      techGroup {
        groupId
        name
      }
      technician {
        userId
        name
        firstName
        lastName
      }
    }
  }
`;

const ADD_TASK_VIEW = gql`
  mutation($input: ListViewInput) {
    addView(input: $input) {
      id
      name
      module
      viewCondition
      shared
    }
  }
`;

const GET_TASK_VIEW = gql`
  query($id: ID!) {
    getTaskView(id: $id) {
      id
      name
      viewCondition
      customView
      shared
    }
  }
`;

const UPDATE_TASK_VIEW = gql`
  mutation($id: ID!, $input: ListViewInput!) {
    updateView(id: $id, input: $input) {
      id
      name
      module
      viewCondition
      shared
      customView
    }
  }
`;

const DELETE_TASK_VIEW = gql`
  mutation($id: ID!, $input: ListViewInput!) {
    deleteView(id: $id, input: $input)
  }
`;

const UPDATE_TASKS = gql`
  mutation($input: UpdateTasksInput!) {
    updateTasks(input: $input) {
      taskOrder
      createdTime
      taskId
      displayId
      title
      description
      status
      priority
      estimatedTime
      scheduledStartDate
      dueDate
      overdue
      actualStartDate
      actualEndDate
      technician {
        userId
        name
        email
        firstName
        lastName
      }
      milestone {
        milestoneId
      }
      techGroup {
        name
        groupId
      }
      ticket {
        ticketId
        subject
        displayId
        loggedInUserAsFollower {
          ticketPermission
        }
      }
      module
      project {
        projectId
        title
        loggedInUserRole {
          roleId
        }
      }
      workItem {
        workId
        displayId
        subject
        module
        client {
          accountId
          name
        }
        site {
          name
          id
        }
      }
      ticketPermission
    }
  }
`;

const UPDATE_MILESTONES = gql`
  mutation($input: [MilestoneInput]) {
    updateMilestones(input: $input) {
      milestoneId
      name
      status
      actualEndDate
      totalTasks
      plannedTasks
      inProgressTasks
      blockedTasks
      completedTasks
      overdueTasks
      milestoneOrder
    }
  }
`;

export {
  CREATE_TASK,
  UPDATE_TASK,
  DELETE_TASK,
  CREATE_TASKS,
  CREATE_MILESTONE_TASK,
  DELETE_TASK_TEMPLATE,
  UPDATE_TASK_TEMPLATE,
  CREATE_TASK_TEMPLATE,
  ADD_TASK_VIEW,
  UPDATE_TASK_VIEW,
  GET_TASK_VIEW,
  DELETE_TASK_VIEW,
  UPDATE_TASKS,
  UPDATE_MILESTONES
};
